import { Totem } from "./Totem.js";
import spreadFrame from "../assets/brand/macet/spread-frame.svg";
import stampImg from "../assets/brand/macet/stamp-compact.png";
import styles from "./PassportBook.module.css";

interface Props {
  displayNumber: string;
  cityName: string;
  totem: string;
  childName: string;
  issueDate?: string; // YYYY-MM-DD
  rank?: string;
  // Number of completed quests — one mini wax stamp is rendered per quest
  // in the rank slot. New passport = 1 by default; M5 will supply the real
  // value derived from ClientAttendance.
  questsCount?: number;
  photoUrl?: string | null;
}

// The owner slot in the passport is a small rectangle that only comfortably
// fits a single word. Parents type ФИО in the form ("Иван Иванович Иванов"),
// so we display just the given name on the document itself. The full name
// stays in the DB / CRM payload.
function firstName(fullName: string): string {
  // Split on any Unicode whitespace so NBSP / tabs / weird spaces from
  // form autocomplete also count as separators — otherwise "Иван
  // Иванович Иванов" would render as one long word and clip the slot.
  const parts = fullName.trim().split(/\s+/);
  return parts[0] ?? fullName.trim();
}

// Cap the visible stamp count so a veteran passport doesn't overflow the
// underline slot. Beyond MAX we could add a "+N" badge later, but for now
// (M3/M4) real value is always 1.
const MAX_STAMPS = 6;

const MONTHS = [
  "января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря",
];

function formatDate(iso?: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  const mi = Math.max(0, Math.min(11, Number(m) - 1));
  return `${Number(d)} ${MONTHS[mi]} ${y}`;
}

// Issued passport = the designer's Frame 2 SVG (spread-frame.svg, 320×656
// macet with all decorations + placeholder underlines baked in). We render
// runtime overlays on top at the exact pixel coords from the placeholder
// rects — see PassportBook.module.css for the coordinate table.
export function PassportBook(props: Props) {
  const rank = props.rank ?? "Новичок";
  const quests = Math.max(1, props.questsCount ?? 1);
  const stampCount = Math.min(quests, MAX_STAMPS);
  const displayName = firstName(props.childName);
  return (
    <div
      className={styles.frame}
      aria-label={`Паспорт искателя ${props.displayNumber}, ${props.childName}`}
    >
      <img
        src={spreadFrame}
        alt=""
        className={styles.bg}
        aria-hidden
        draggable={false}
      />

      {/* Big wax seal — placeholder in Frame 2 is filled by a pattern
          that references the raster we stripped for weight, so we paint
          the real asset here as an overlay. The macet had a second,
          smaller decorative stamp on the left column, but it doubled
          with the first runtime rank stamp — dropped in favour of the
          rank stamps sitting next to the "Ранг" value. */}
      <img
        src={stampImg}
        alt=""
        className={styles.stamp}
        aria-hidden
        draggable={false}
      />

      {/* Avatar photo (only if uploaded). Covers the grey placeholder
          in the SVG at (66.9, 382.42, 71.87×80.8). Falls back to the
          totem centered inside the grey rect. */}
      {/* Intro paragraph — the SVG's baked version was stripped so we
          can edit copy without regenerating the macet from Figma. */}
      <p className={styles.paragraph}>
        Ты погрузился в поразительный мир приключений и прошел все
        испытания квеста, заслужив свой первый паспорт – паспорт
        искателя. Носи его с гордостью, повышай свой ранг и делись
        с друзьями!
      </p>

      <div className={styles.avatar}>
        {props.photoUrl ? (
          <img
            src={props.photoUrl}
            alt={`Фото ${props.childName}`}
            className={styles.avatarImg}
          />
        ) : (
          <Totem code={props.totem} size={64} />
        )}
      </div>

      {/* Runtime text overlays. All positions are % of frame 320×656
          → cqw. Colors follow dump (#681617 for labels, #D32C05 for
          the owner name). */}

      {/* № ПАСПОРТА label — the macet ships the "№" as a separate
          icon-svg placed 4-5 px below the "ПАСПОРТА" baseline, which
          reads as a "floating" tab. We mask the baked pair with a
          card-coloured rect and repaint the label as one <span> so the
          symbol and word sit on a single baseline. */}
      <div className={styles.numLabel} aria-hidden>
        № ПАСПОРТА
      </div>

      <div className={styles.numValue}>{props.displayNumber}</div>
      <div className={styles.ownerName}>{displayName}</div>
      <div className={styles.rankValue}>
        <span className={styles.rankLabel}>{rank}</span>
      </div>
      {/* Quest stamps — one per completed quest, laid out in a row on
          its own line below the rank underline. Anchored at the small
          stamp slot the designer placed in Frame 2. */}
      <div
        className={styles.questStamps}
        aria-label={`Пройдено квестов: ${quests}`}
      >
        {Array.from({ length: stampCount }).map((_, i) => (
          <img
            key={i}
            src={stampImg}
            alt=""
            className={styles.questStamp}
            aria-hidden
            draggable={false}
          />
        ))}
      </div>
      <div className={styles.dateValue}>{formatDate(props.issueDate)}</div>
      <div className={styles.placeValue}>{props.cityName}</div>
    </div>
  );
}
